import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Zap, Sparkles, CheckCircle2, Crown } from 'lucide-react';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'stripe-pricing-table': any;
    }
  }
}

export default function Upgrade() {
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: 'calc(100vh - 64px)', padding: '0 1.5rem 3rem', overflowX: 'hidden' }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        
        {/* Header */}
        <div style={{ padding: '2.5rem 0 2rem' }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              background: 'none', border: 'none', color: 'var(--text-muted)',
              cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '1.5rem',
            }}
            onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = 'var(--text-primary)'}
            onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = 'var(--text-muted)'}
          >
            <ArrowLeft size={14} /> Back
          </button>
          
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '0.75rem' }}>
            <div style={{
              width: '40px', height: '40px', background: 'rgba(255,170,0,0.1)',
              border: '1px solid rgba(255,170,0,0.3)', borderRadius: '10px',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <Crown size={20} color="var(--color-accent)" />
            </div>
            <h1 style={{ fontSize: '2.2rem', fontWeight: 800 }}>
              Upgrade to <span style={{ color: 'var(--color-accent)' }}>ElectrAI Pro</span>
            </h1>
          </div>
          <p style={{ color: 'var(--text-secondary)', maxWidth: '580px', fontSize: '1rem', lineHeight: 1.6 }}>
            Unlock unlimited AI diagnostics, save your maker projects, and get priority access to our master-tier electronics reasoning engine.
          </p>
        </div>

        {/* Pricing Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem', marginBottom: '4rem' }}>
          {[
            {
              title: 'UNLIMITED DIAGNOSTICS',
              desc: 'No more daily limits. Ask ElectrAI as many troubleshooting questions as you need to fix your circuit.',
              icon: Zap,
              color: 'green'
            },
            {
              title: 'MASTER-TIER REASONING',
              desc: 'Access our most advanced AI models for complex PCB analysis, signal processing, and firmware debugging.',
              icon: Sparkles,
              color: 'cyan'
            },
            {
              title: 'SECURE PROJECT VAULT',
              desc: 'Save your component lists, schematics, and project guides in a secure workspace synced across devices.',
              icon: ShieldCheck,
              color: 'purple'
            }
          ].map((f, i) => {
            const colors: Record<string, string> = {
              green: 'var(--color-primary)',
              cyan: 'var(--color-secondary)',
              purple: 'var(--color-purple)'
            };
            return (
              <div key={i} style={{
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-color)',
                padding: '1.5rem',
                borderRadius: '16px',
                transition: 'border-color 0.2s ease',
              }}
                onMouseEnter={e => (e.currentTarget as HTMLElement).style.borderColor = `${colors[f.color]}33`}
                onMouseLeave={e => (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-color)'}
              >
                <div style={{
                  width: '40px', height: '40px', background: `${colors[f.color]}15`,
                  borderRadius: '10px', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', marginBottom: '1rem', border: `1px solid ${colors[f.color]}33`
                }}>
                  <f.icon size={18} color={colors[f.color]} />
                </div>
                <h3 style={{ fontSize: '0.82rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '0.5rem', letterSpacing: '0.05em' }}>{f.title}</h3>
                <p style={{ color: 'var(--text-primary)', opacity: 0.9, fontSize: '0.85rem', lineHeight: 1.6 }}>{f.desc}</p>
              </div>
            );
          })}
        </div>

        {/* Plan Tiers Preview */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '3rem' }}>
          <div style={{
            background: 'rgba(0,207,255,0.05)',
            border: '1px solid rgba(0,207,255,0.25)',
            borderRadius: '20px',
            padding: '1.75rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}>
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--color-secondary)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem' }}>One-Time Repair Sprint</div>
              <h3 style={{ fontSize: '1.4rem', fontWeight: 800, marginBottom: '0.25rem' }}>7-Day Repair Pass</h3>
              <div style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '1rem' }}>
                $4.99 <span style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-secondary)' }}>/ 7 days</span>
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', lineHeight: 1.6, marginBottom: '1rem' }}>
                Fix your current project without committing to a recurring subscription. Full access to AI diagnostics, schematics & BOM wizard for 7 full days.
              </p>
            </div>
            <a
              href="https://billing.stripe.com/p/login/9B614maCtcm9dp1enYgfu00"
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                background: 'var(--color-secondary)', color: '#050912', padding: '0.75rem', borderRadius: '10px',
                fontWeight: 700, fontSize: '0.88rem', textDecoration: 'none', textAlign: 'center'
              }}
            >
              Get 7-Day Pass ($4.99)
            </a>
          </div>

          <div style={{
            background: 'rgba(0,255,136,0.06)',
            border: '1px solid rgba(0,255,136,0.3)',
            borderRadius: '20px',
            padding: '1.75rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute', top: '-12px', right: '20px',
              background: 'var(--color-primary)', color: '#050912',
              fontSize: '0.65rem', fontWeight: 900, textTransform: 'uppercase',
              letterSpacing: '0.1em', padding: '0.2rem 0.6rem', borderRadius: '999px'
            }}>
              Most Popular
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--color-primary)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem' }}>Full Workbench Access</div>
              <h3 style={{ fontSize: '1.4rem', fontWeight: 800, marginBottom: '0.25rem' }}>ElectrAI Pro Monthly</h3>
              <div style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '1rem' }}>
                $9.99 <span style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-secondary)' }}>/ month</span>
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', lineHeight: 1.6, marginBottom: '1rem' }}>
                Unlimited diagnostics, saved projects in Vault, advanced PCB reasoning, studio code generator, and verified schematics.
              </p>
            </div>
            <a
              href="https://billing.stripe.com/p/login/9B614maCtcm9dp1enYgfu00"
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                background: 'var(--color-primary)', color: '#050912', padding: '0.75rem', borderRadius: '10px',
                fontWeight: 700, fontSize: '0.88rem', textDecoration: 'none', textAlign: 'center'
              }}
            >
              Start Pro Access ($9.99/mo)
            </a>
          </div>
        </div>

        {/* Pricing Table Wrapper */}
        <div style={{
          background: 'var(--bg-surface)',
          border: '1px solid rgba(0,255,136,0.15)',
          borderRadius: '24px',
          padding: '2rem',
          boxShadow: '0 0 40px rgba(0,255,136,0.04)',
          marginBottom: '3rem',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
            width: '100%', height: '100px', background: 'linear-gradient(180deg, rgba(0,255,136,0.03) 0%, transparent 100%)',
            pointerEvents: 'none'
          }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <stripe-pricing-table
              pricing-table-id="prctbl_1Solk9CXDvmxKfrenOQyFnFF"
              publishable-key="pk_live_51JEMmMCXDvmxKfreuwIye2xPXnCQsn0R8zC4oReSUDeeIVOTGhpPThDgySuop659QOkz4mR8NNOauFdqydy7orMb00ApoSaVX4"
            />
          </div>
        </div>

        {/* Footer info */}
        <div style={{ textAlign: 'center', paddingBottom: '2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.82rem', marginBottom: '1rem' }}>
            <CheckCircle2 size={14} color="var(--color-primary)" />
            Secure payment processing via Stripe
          </div>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
            Already subscribed? <a href="https://billing.stripe.com/p/login/9B614maCtcm9dp1enYgfu00" style={{ color: 'var(--color-primary)', textDecoration: 'none', fontWeight: 600 }}>Manage Billing</a>
          </p>
        </div>
      </div>
    </div>
  );
}
